import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PDFDocument } from 'pdf-lib';
import ReactDOM from 'react-dom/client';
import React from 'react';

// SHI-564 — every production sheet and ingredient label prints a best-before date, and the
// rule is the same everywhere: the day the order was prepared, plus two years, as dd.mm.yyyy.
// For manual and external orders that base is `preparation_date`, which is not always usable:
// manual orders never had one written (all 1321 prod rows are NULL) and a few external rows
// arrived from partners as 0000-00-00. `new Date(null)` is the epoch and `new Date('0000-00-00')`
// is Invalid, so those two cases printed "01.01.1972" and "NaN.NaN.NaN" on a physical label.
// Fall back to when the order was created — the closest honest stand-in for the day it was
// prepared — so a missing date can never reach the print.
export const PRODUCT_SHELF_LIFE_YEARS = 2;

export const resolvePreparationDate = (order) => {
  const candidates = [order?.preparation_date, order?.created_at, order?.createdAt];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const parsed = new Date(candidate);
    // Rejects both Invalid Date (0000-00-00) and the epoch that `new Date(null)` yields.
    if (!Number.isNaN(parsed.getTime()) && parsed.getFullYear() > 1970) return parsed;
  }
  return new Date();
};

export const formatPrintedDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-based
  const day = String(date.getDate()).padStart(2, '0');
  return `${day}.${month}.${year}`;
};

export const getPreparationDate = (order) => formatPrintedDate(resolvePreparationDate(order));

export const getBestBeforeDate = (order) => {
  const base = resolvePreparationDate(order);
  const expiry = new Date(base);
  expiry.setFullYear(base.getFullYear() + PRODUCT_SHELF_LIFE_YEARS);
  return formatPrintedDate(expiry);
};

const convertJSONToCSV = (jsonData, columnHeaders) => {
  if (jsonData.length === 0) {
    return '';
  }
  const headers = columnHeaders.join(',') + '\n';
  const rows = jsonData
    .map((row) => {
      return columnHeaders.map((field) => row[field] || '').join(',');
    })
    .join('\n');
  return headers + rows;
};

export const downloadCsv = (jsonData, headers) => {
  const csvData = convertJSONToCSV(jsonData, headers);

  if (csvData === '') {
    return;
  } else {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'Ingredients.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const downloadPdf = async (pageRefs, orderId) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();

  for (let i = 0; i < pageRefs.length; i++) {
    const canvas = await html2canvas(pageRefs[i].current, {
      scale: 2,
      useCORS: true,
      logging: true,
    });
    const imgData = canvas.toDataURL('image/jpeg');
    const imgProps = {
      width: pageWidth,
      height: (canvas.height * pageWidth) / canvas.width,
    };

    if (i !== 0) pdf.addPage();
    pdf.addImage(imgData, 'JPEG', 0, 0, imgProps.width, imgProps.height);
  }
  pdf.addPage();

  pdf.save(`riise_order_${orderId}.pdf`);
};

export async function mergePdfs(pdfBuffers) {
  const mergedPdf = await PDFDocument.create();

  for (const buffer of pdfBuffers) {
    const pdf = await PDFDocument.load(buffer);
    const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  return await mergedPdf.save(); // Uint8Array
}

export const downloadMultipleOrderPdfs = async (orders, generateOrderPdf, filename) => {
  try {
    const pdfBuffers = [];

    for (const order of orders) {
      // Generate PDF for each order
      const pdfBuffer = await generateOrderPdf(order);
      pdfBuffers.push(pdfBuffer);
    }

    // Merge all PDFs
    const mergedPdfBytes = await mergePdfs(pdfBuffers);

    // Create blob and download
    const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || `riise_orders_${Date.now()}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating multiple PDFs:', error);
    throw error;
  }
};

export const generateOrderPdfBuffer = async (order, ingredientPrices, focusAreas) => {
  try {
    return new Promise((resolve, reject) => {
      // Create a temporary container
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '794px';
      tempContainer.style.fontFamily = 'Barlow';
      document.body.appendChild(tempContainer);

      // Import the component dynamically
      import('../Components/Web/OrderPdfGenerator')
        .then(({ default: OrderPdfGenerator }) => {
          // Create React root and render the component
          const root = ReactDOM.createRoot(tempContainer);

          const handleReady = async (pageRefs) => {
            try {
              // Wait a bit more for images to load
              await new Promise((resolve) => setTimeout(resolve, 1000));

              // Generate PDF
              const pdf = new jsPDF('p', 'mm', 'a4');
              const pageWidth = pdf.internal.pageSize.getWidth();

              for (let i = 0; i < pageRefs.length; i++) {
                const canvas = await html2canvas(pageRefs[i].current, {
                  scale: 2,
                  useCORS: true,
                  logging: false,
                  allowTaint: true,
                });
                const imgData = canvas.toDataURL('image/jpeg');
                const imgProps = {
                  width: pageWidth,
                  height: (canvas.height * pageWidth) / canvas.width,
                };

                if (i !== 0) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, 0, imgProps.width, imgProps.height);
              }

              // Add an empty page after each order's PDF
              pdf.addPage();

              // Get PDF as buffer
              const pdfBuffer = pdf.output('arraybuffer');

              // Clean up
              root.unmount();
              document.body.removeChild(tempContainer);

              resolve(pdfBuffer);
            } catch (error) {
              // Clean up on error
              root.unmount();
              document.body.removeChild(tempContainer);
              reject(error);
            }
          };

          root.render(
            React.createElement(OrderPdfGenerator, {
              order,
              ingredientPrices,
              focusAreas,
              onReady: handleReady,
            })
          );
        })
        .catch(reject);
    });
  } catch (error) {
    console.error('Error generating order PDF buffer:', error);
    throw error;
  }
};

export const mergeAndDownloadShipmentLabels = async (labels, filename) => {
  const mergedPdf = await PDFDocument.create();

  for (const label of labels) {
    const response = await fetch(label);
    const pdfBytes = await response.arrayBuffer();
    const pdf = await PDFDocument.load(pdfBytes);

    const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  const mergedBytes = await mergedPdf.save();
  const blob = new Blob([mergedBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'merged-labels.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// `options.showPreparationDate` adds a Preparation date row to the label table. Only the
// manual-orders screen turns it on (SHI-564), so external partner sheets print exactly as before.
export const generateExternalOrderPdfBuffer = async (order, ingredientPrices, ingredientCodes, options = {}) => {
  try {
    return new Promise((resolve, reject) => {
      // Create a temporary container
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '794px';
      tempContainer.style.fontFamily = 'Barlow';
      document.body.appendChild(tempContainer);

      // Import the component dynamically
      (order?.user?.source_company_name?.includes('gettested')
        ? import('../Components/External/GettestedOrderPdfGenerator')
        : order?.user?.source_company_name?.includes('biovariance')
        ? import('../Components/External/BiovarianceOrderPdfGenerator')
        : import('../Components/External/ExternalOrderPdfGenerator')
      )
        .then(({ default: ExternalOrderPdfGenerator }) => {
          // Create React root and render the component
          const root = ReactDOM.createRoot(tempContainer);

          const handleReady = async (pageRefs) => {
            try {
              // Wait a bit more for images to load
              await new Promise((resolve) => setTimeout(resolve, 1000));

              // Generate PDF
              const pdf = new jsPDF('p', 'mm', 'a4');
              const pageWidth = pdf.internal.pageSize.getWidth();

              for (let i = 0; i < pageRefs.length; i++) {
                const canvas = await html2canvas(pageRefs[i].current, {
                  scale: 2,
                  useCORS: true,
                  logging: false,
                  allowTaint: true,
                });
                const imgData = canvas.toDataURL('image/jpeg');
                const imgProps = {
                  width: pageWidth,
                  height: (canvas.height * pageWidth) / canvas.width,
                };

                if (i !== 0) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, 0, imgProps.width, imgProps.height);
              }

              // Add an empty page after each order's PDF if not gettested
              // if (!order?.user?.source_company_name?.includes('gettested')) pdf.addPage();
              pdf.addPage();
              // Get PDF as buffer
              const pdfBuffer = pdf.output('arraybuffer');

              // Clean up
              root.unmount();
              document.body.removeChild(tempContainer);

              resolve(pdfBuffer);
            } catch (error) {
              // Clean up on error
              root.unmount();
              document.body.removeChild(tempContainer);
              reject(error);
            }
          };

          root.render(
            React.createElement(ExternalOrderPdfGenerator, {
              order,
              ingredientPrices,
              ingredientCodes,
              showPreparationDate: !!options.showPreparationDate,
              onReady: handleReady,
            })
          );
        })
        .catch(reject);
    });
  } catch (error) {
    console.error('Error generating order PDF buffer:', error);
    throw error;
  }
};

export const generateLabelPdfBuffer = async (order, ingredientPrices) => {
  try {
    return new Promise((resolve, reject) => {
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '794px';
      tempContainer.style.fontFamily = 'Helvetica, sans-serif';
      document.body.appendChild(tempContainer);

      import('../Components/Web/LabelIngredients4x6Generator')
        .then(({ default: LabelIngredients4x6Generator }) => {
          // Create React root and render the component
          const root = ReactDOM.createRoot(tempContainer);

          const handleReady = async (pageRefs) => {
            try {
              // Wait a bit for rendering to complete
              await new Promise((resolve) => setTimeout(resolve, 500));

              // Generate PDF with 4x6 inch dimensions (98.5mm x 149mm)
              const pdf = new jsPDF('p', 'mm', [98.5, 149]);
              const pageWidth = pdf.internal.pageSize.getWidth();
              const pageHeight = pdf.internal.pageSize.getHeight();

              for (let i = 0; i < pageRefs.length; i++) {
                const canvas = await html2canvas(pageRefs[i].current, {
                  scale: 2,
                  useCORS: true,
                  logging: false,
                  allowTaint: true,
                });
                const imgData = canvas.toDataURL('image/jpeg', 0.75);

                // Calculate dimensions to fit the 4x6 page
                const imgProps = {
                  width: pageWidth,
                  height: (canvas.height * pageWidth) / canvas.width,
                };

                // If height exceeds page height, scale down to fit
                if (imgProps.height > pageHeight) {
                  imgProps.height = pageHeight;
                  imgProps.width = (canvas.width * pageHeight) / canvas.height;
                }

                if (i !== 0) pdf.addPage([98.5, 149]);
                pdf.addImage(imgData, 'JPEG', 0, 0, imgProps.width, imgProps.height);
              }
              const pdfBuffer = pdf.output('arraybuffer');

              root.unmount();
              document.body.removeChild(tempContainer);

              resolve(pdfBuffer);
            } catch (error) {
              root.unmount();
              document.body.removeChild(tempContainer);
              reject(error);
            }
          };

          root.render(
            React.createElement(LabelIngredients4x6Generator, {
              order,
              ingredientPrices,
              onReady: handleReady,
            })
          );
        })
        .catch(reject);
    });
  } catch (error) {
    console.error('Error generating label PDF buffer:', error);
    throw error;
  }
};

export const extractTrackingId = (url) => {
  const parsedUrl = new URL(url?.includes('https') ? url : 'https://weriise.co');
  return parsedUrl.searchParams.get('tracking-id');
};

export const generateExternalLabelPdfBuffer = async (order, ingredientPrices, ingredientCodes) => {
  try {
    return new Promise((resolve, reject) => {
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '794px';
      tempContainer.style.fontFamily = 'Helvetica, sans-serif';
      document.body.appendChild(tempContainer);

      import('../Components/External/ExternalOrderLabelIngredients4x6Generator')
        .then(({ default: ExternalOrderLabelIngredients4x6Generator }) => {
          const root = ReactDOM.createRoot(tempContainer);

          const handleReady = async (pageRefs) => {
            try {
              await new Promise((resolve) => setTimeout(resolve, 500));

              const pdf = new jsPDF('p', 'mm', [98.5, 149]);
              const pageWidth = pdf.internal.pageSize.getWidth();
              const pageHeight = pdf.internal.pageSize.getHeight();

              for (let i = 0; i < pageRefs.length; i++) {
                const canvas = await html2canvas(pageRefs[i].current, {
                  scale: 2,
                  useCORS: true,
                  logging: false,
                  allowTaint: true,
                });
                const imgData = canvas.toDataURL('image/jpeg', 0.75);

                const imgProps = {
                  width: pageWidth,
                  height: (canvas.height * pageWidth) / canvas.width,
                };

                if (imgProps.height > pageHeight) {
                  imgProps.height = pageHeight;
                  imgProps.width = (canvas.width * pageHeight) / canvas.height;
                }

                if (i !== 0) pdf.addPage([98.5, 149]);
                pdf.addImage(imgData, 'JPEG', 0, 0, imgProps.width, imgProps.height);
              }
              const pdfBuffer = pdf.output('arraybuffer');

              root.unmount();
              document.body.removeChild(tempContainer);

              resolve(pdfBuffer);
            } catch (error) {
              root.unmount();
              document.body.removeChild(tempContainer);
              reject(error);
            }
          };

          root.render(
            React.createElement(ExternalOrderLabelIngredients4x6Generator, {
              order,
              ingredientPrices,
              ingredientCodes,
              onReady: handleReady,
            })
          );
        })
        .catch(reject);
    });
  } catch (error) {
    console.error('Error generating external label PDF buffer:', error);
    throw error;
  }
};
